import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Loader2, ArrowLeft } from "lucide-react";
import { apiClient, formatApiError, TOKEN_KEY } from "@/lib/api";
import { toast } from "sonner";

export default function AdminLogin() {
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        document.title = "Admin Sign In · Pacific Key Management";
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) navigate("/admin/dashboard", { replace: true });
    }, [navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await apiClient.post("/auth/login", form);
            localStorage.setItem(TOKEN_KEY, data.access_token);
            toast.success("Welcome back.");
            navigate("/admin/dashboard");
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || "Unable to sign in.";
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-navy flex flex-col" data-testid="admin-login-page">
            <header className="container-luxe py-8 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3 text-pearl/80 hover:text-gold transition-colors text-[0.7rem] uppercase tracking-[0.28em]" data-testid="login-back-home">
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Back to site
                </Link>
                <div className="text-right">
                    <p className="font-serif text-pearl text-xl tracking-wide">Pacific Key</p>
                    <p className="text-[0.6rem] tracking-[0.32em] uppercase text-gold/80">Management</p>
                </div>
            </header>

            <div className="flex-1 flex items-center justify-center px-6 pb-20">
                <div className="w-full max-w-md bg-pearl p-10 sm:p-12 border border-gold/20">
                    <div className="flex justify-center mb-6">
                        <div className="w-12 h-12 border border-gold/40 flex items-center justify-center">
                            <Lock className="w-5 h-5 text-gold" strokeWidth={1.25} />
                        </div>
                    </div>
                    <p className="overline text-gold text-center">Private Estate Office</p>
                    <h1 className="mt-4 font-serif text-3xl sm:text-4xl text-center text-balance">
                        Admin Sign In
                    </h1>
                    <p className="mt-3 text-sm text-navy/65 text-center">Authorized personnel only.</p>

                    <form onSubmit={submit} className="mt-10 space-y-7" data-testid="admin-login-form">
                        <label className="block">
                            <span className="overline text-navy/55">Email</span>
                            <input
                                required
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="input-luxe"
                                data-testid="admin-login-email"
                                placeholder="admin@pacifickeymanagement.com"
                            />
                        </label>
                        <label className="block">
                            <span className="overline text-navy/55">Password</span>
                            <input
                                required
                                type="password"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                className="input-luxe"
                                data-testid="admin-login-password"
                                placeholder="••••••••"
                            />
                        </label>

                        {error && (
                            <p className="text-sm text-red-600 tracking-wide" data-testid="admin-login-error">{error}</p>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-luxe-primary w-full justify-center"
                            data-testid="admin-login-submit"
                        >
                            {loading ? (
                                <><Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> Signing in</>
                            ) : "Sign In"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
